import React, { useMemo, useState } from "react";
import { Box, IconButton, Paper, Switch, Tooltip, useTheme } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ScheduleIcon from "@mui/icons-material/Schedule";
import RepeatIcon from "@mui/icons-material/Repeat";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { DataGridPro, GridToolbar, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";
import { tokens } from "../../../assets/theme";
import { useIntl } from "react-intl";
import { hasAnyRole } from "../../../auth/authUi";
import { useAuth } from "../../../auth/AuthContext";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import { getFrequencyTypeLabel, getTriggerBasisLabel, ScheduleTriggerBasis } from "../scheduleEnums";
import { CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import { getAssetHierarchyPalette, getDataGridPanelSx, getSwitchSx } from "../../cmms/assetHierarchyStyles";
import { applyColumnWidths, readStoredJson, reorderColumns } from "../../cmms/cmmsGridPrefs";
import { formatTimestamp } from "../../cmms/cmmsFormat";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";

const columnOrderKey = "cmmsRecurringSchedules-columnOrder";
const columnWidthsKey = "cmmsRecurringSchedules-columnWidths";
// The third of the same kind, as it already is on the other two CMMS grids. Order and
// width have been remembered here from the start, and which columns you want is the same sort of
// preference as how wide you want them.
const columnVisibilityKey = "cmmsRecurringSchedules-columnVisibility";

const initFromStorage = (cols) => {
  let out = cols;
  const order = readStoredJson(columnOrderKey, null);
  if (order) out = reorderColumns(order, out);
  const widths = readStoredJson(columnWidthsKey, null);
  if (widths) out = applyColumnWidths(widths, out);
  return out;
};

/*
 * The local formatDate that used to sit here called `new Date(value).toLocaleString()` with no
 * locale, which reads the *browser's* language rather than the app's — so a Swedish user on an
 * English-defaulting browser got AM/PM in a grid whose sibling columns were Swedish. That is the
 * anti-pattern cmmsFormat.js was created to remove (see its header); this grid was missed in the
 * sweep, which is why the work orders grid beside it was already right.
 */
export default function RecurringSchedulesDataGrid({
  schedules,
  isLoading,
  assetNodeNameById,
  onEdit,
  onDelete,
  onToggleActive,
  onOpenReset,
  emptyState,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const colors = tokens(theme.palette.mode);
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const apiRef = useGridApiRef();
  const { roleIds } = useAuth();
  const language = localStorage.getItem("language") || "en";
  const translations = language === "sv" ? svSE : enUS;
  // Lazy initialiser so the read happens once rather than on every render, and guarded like the
  // two sibling keys: a value that will not parse costs a preference, not the page.
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() =>
    readStoredJson(columnVisibilityKey, { id: false }));

  // Resolved here rather than threaded from the panel, the way SparePartsDataGrid does it: the
  // grid owns the cells it renders, so it owns the question of which of them it may render.
  const canManage = hasAnyRole(roleIds, CMMS_MANAGER_ROLES);

  const baseColumns = useMemo(
    () => [
      { field: "id", headerName: intl.formatMessage(messages.columnId), width: 80 },
      { field: "title", headerName: intl.formatMessage(messages.columnTitle), flex: 2, minWidth: 180 },
      {
        field: "assetNodeName",
        headerName: intl.formatMessage(messages.columnAsset),
        flex: 1,
        minWidth: 140,
        valueGetter: (params) =>
          assetNodeNameById?.get(params.row.assetNodeId) ?? `#${params.row.assetNodeId}`,
      },
      {
        field: "toolName",
        headerName: intl.formatMessage(messages.columnTool),
        flex: 1,
        minWidth: 120,
        // Blank on an asset schedule. Resolved by the service, so the grid needs no tool list.
        valueGetter: (params) => params.row.toolName ?? "",
      },
      {
        field: "triggerBasis",
        headerName: intl.formatMessage(messages.columnBasis),
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        // The cell is an icon, so without a value of its own the CSV carries the raw enum integer.
        // Mounting the toolbar gave this column an Export button and made that visible.
        valueGetter: (params) =>
          getTriggerBasisLabel(params.row.triggerBasis ?? ScheduleTriggerBasis.Calendar, intl),
        renderCell: (params) => {
          const basis = params.row.triggerBasis ?? ScheduleTriggerBasis.Calendar;
          const Icon =
            basis === ScheduleTriggerBasis.RunningTime
              ? ScheduleIcon
              : basis === ScheduleTriggerBasis.Quantity
                ? RepeatIcon
                : CalendarMonthIcon;
          return (
            <Tooltip title={params.value}>
              <Icon fontSize="small" sx={{ color: colors.gray[100] }} />
            </Tooltip>
          );
        },
      },
      {
        field: "frequencyType",
        headerName: intl.formatMessage(messages.columnFrequency),
        width: 160,
        valueGetter: (params) => {
          const basis = params.row.triggerBasis ?? ScheduleTriggerBasis.Calendar;
          if (basis === ScheduleTriggerBasis.RunningTime) {
            return intl.formatMessage(messages.everyNHours, { n: params.row.runningTimeInterval });
          }
          if (basis === ScheduleTriggerBasis.Quantity) {
            return intl.formatMessage(messages.everyNCycles, { n: params.row.quantityInterval });
          }
          const label = getFrequencyTypeLabel(params.row.frequencyType, intl);
          const interval = params.row.frequencyInterval;
          return interval > 1 ? `${label} (×${interval})` : label;
        },
      },
      {
        field: "nextRunDate",
        headerName: intl.formatMessage(messages.columnNextRun),
        width: 180,
        // valueFormatter, not valueGetter: the cell value stays the raw ISO string, so the column
        // sorts chronologically. A valueGetter here would sort on the formatted text, and in
        // English that is "MM/DD/YYYY" — January 2027 would sort above December 2025.
        valueFormatter: (params) => formatTimestamp(intl, params.value),
      },
      {
        field: "isActive",
        headerName: intl.formatMessage(messages.columnActive),
        width: 100,
        /*
         * Disabled rather than hidden, which is the one place this grid parts company with the
         * spare parts one. Whether a schedule is running is Cmms_Field information — a technician
         * looking at a machine that has not been serviced needs to see that its schedule is
         * switched off — and this switch is the only place the list shows it. Hiding it would take
         * away the reading along with the control.
         */
        renderCell: (params) => (
          <Box onClick={(event) => event.stopPropagation()}>
            <Switch
              size="small"
              checked={Boolean(params.row.isActive)}
              disabled={!canManage}
              onChange={() => onToggleActive?.(params.row)}
              sx={getSwitchSx(cmmsColors)}
            />
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: canManage ? 140 : 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        // Kept out of the Columns panel. With no headerName it would list as a blank
        // unlabelled switch, and switching it off would quietly remove Edit and Delete.
        hideable: false,
        // And out of the CSV, where it is an unnamed column of nothing.
        disableExport: true,
        renderCell: (params) => (
          <Box onClick={(event) => event.stopPropagation()}>
            {/*
              * Usage schedules only. A calendar schedule counts nothing, so there is no counter
              * to look at and no reset to record — the endpoint answers 400.
              *
              * Not role-gated here: what this opens is the counter and its history, which is
              * Cmms_Field. The reset inside the drawer carries the Cmms_Manager gate, and a
              * technician who can see when a tool was last serviced but not reset it is the
              * split the module has everywhere else.
              */}
            {(params.row.triggerBasis ?? ScheduleTriggerBasis.Calendar) !== ScheduleTriggerBasis.Calendar &&
              <Tooltip title={intl.formatMessage(messages.gridResetAria)}>
                <IconButton
                  size="small"
                  onClick={() => onOpenReset?.(params.row)}
                  // No trailing margin when it is the only button in the cell, which is what a
                  // technician sees.
                  sx={{ mr: canManage ? 1 : 0, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
                >
                  <RestartAltIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            }
            {/*
              * Editing a schedule is Cmms_Manager, and so is deleting one: a schedule raises work
              * orders unattended, so retiming or removing one changes what the module does on its
              * own. Hidden rather than disabled, as everywhere else in the module — a control that
              * always 403s is a control that lies about what its owner may do.
              */}
            {canManage &&
              <Tooltip title={intl.formatMessage(cmmsMessages.edit)}>
                <IconButton
                  size="small"
                  onClick={() => onEdit?.(params.row)}
                  sx={{ backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            }
            {canManage &&
              <Tooltip title={intl.formatMessage(cmmsMessages.delete)}>
                <IconButton
                  size="small"
                  onClick={() => onDelete?.(params.row)}
                  sx={{ ml: 1, backgroundColor: colors.primary[700], "&:hover": { backgroundColor: colors.primary[500] } }}
                >
                  <DeleteOutlineIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            }
          </Box>
        ),
      },
    ],
    [assetNodeNameById, onEdit, onDelete, onToggleActive, onOpenReset, colors, cmmsColors, intl, canManage],
  );

  const columns = useMemo(() => initFromStorage(baseColumns), [baseColumns]);

  // Mirrors WorkOrdersDataGrid: skeleton body while loading, caller-supplied empty state.
  const gridSlots = useMemo(
    () => ({
      // Columns, Filters, Density and Export — the same toolbar the work orders and spare parts
      // grids mount. Its labels come from the MUI locale already passed below, so this adds no
      // strings of our own.
      toolbar: GridToolbar,
      loadingOverlay: () => (
        <Box sx={{ px: 2, pt: 1.5, width: "100%" }}>
          <CmmsListSkeleton rows={8} height={36} />
        </Box>
      ),
      // Only when the caller supplied one — an overlay rendering `undefined` would cover the grid
      // with nothing.
      ...(emptyState ? {
        noRowsOverlay: () => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
            {emptyState}
          </Box>
        ),
      } : {}),
    }),
    [emptyState],
  );

  const handleColumnOrderChange = () => {
    if (!apiRef.current) return;
    // Every column, not just the visible ones: a hidden column left out here is a hidden column
    // missing from the saved order, and it comes back at the far right once it is unhidden.
    const fields = apiRef.current.getAllColumns().map((c) => c.field);
    localStorage.setItem(columnOrderKey, JSON.stringify(fields));
  };

  const handleColumnResize = (params) => {
    const map = readStoredJson(columnWidthsKey, {});
    map[params.colDef.field] = params.width;
    localStorage.setItem(columnWidthsKey, JSON.stringify(map));
  };

  const handleColumnVisibilityModelChange = (model) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(columnVisibilityKey, JSON.stringify(model));
  };

  return (
    <Paper sx={{ position: "relative", borderRadius: "4px" }}>
      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
            borderRadius: "0px",
            fontSize: "0.95rem",
            border: "0 !important",
          },
          "& .MuiDataGrid-cell": { borderColor: `${colors.primary[700]} !important` },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.primary[400],
            borderColor: `${colors.primary[700]} !important`,
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.primary[600],
          },
          // GridToolbar's buttons default to color="primary", which is the same navy as the grid
          // behind them. The two sibling CMMS grids carry this same rule.
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
          },
        }}
      >
        <DataGridPro
          apiRef={apiRef}
          loading={isLoading}
          slots={gridSlots}
          localeText={translations.components.MuiDataGrid.defaultProps.localeText}
          rows={schedules || []}
          getRowId={(row) => row.id}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnResize={handleColumnResize}
          slotProps={{ panel: { sx: getDataGridPanelSx(theme.palette.mode) } }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
}
